<template>
    <section class="section">
        <div class="container-fluid">
            <div class="columns">
                <div class="column is-12">
                    <h1 class="title">{{ translate("admin_fights_manage.title") }}</h1>
                </div>
            </div>

            <div class="columns hidden-print is-multiline" v-if="initialLoadFinished">
                <div class="column is-12">
                    <div v-if="allSchedules.length === 0" class="notification is-light is-warning">
                        <p class="has-text-weight-bold">
                            {{ translate("admin_fights_manage.no_schedules_available") }}
                        </p>
                        <p>{{ translate("admin_fights_manage.no_schedules_description") }}</p>
                    </div>

                    <template v-else>
                        <div class="mb-4">
                            <label class="label">{{ translate("admin_fights_manage.select_schedule") }}</label>
                            <div class="is-flex is-align-items-center">
                                <ScheduleSelect
                                    style="max-width: 400px"
                                    v-model="selectedScheduleId"
                                    :schedules="allSchedules"
                                    :active-schedule-id="activeSchedule?.id"
                                />
                                <InlineSpinner class="ml-3" v-if="changingScheduleInProgress" />
                            </div>
                        </div>

                        <template v-if="activeSchedule && activeSchedule.id > 0">
                            <MatSelectionBar :event-id="eventId" :preloaded-mats="mats" @selectedMat="selectedMat" />

                            <div class="mt-2" v-if="mats.length > 0">
                                <Checkbox2 v-model="twoColumns">
                                    {{ translate("admin_fights_manage.two_columns") }}
                                </Checkbox2>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
            <div class="columns is-centered" v-else>
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <div class="columns is-multiline">
                <div class="column" :class="getColumnsClass()" v-for="mat in selectedFilteredMats" :key="mat.id">
                    <FightsOnMat
                        :event-id="eventId"
                        :event-timezone="eventTimezone"
                        :mat="mat"
                        :fights-for-mat="fightsForMat[mat.id] ?? []"
                        :fight-queue-status="fightQueueStatusForMat[mat.id]"
                        @refreshFights="refreshFightsForMat"
                        @moveFights="onMoveFights"
                    />
                </div>
            </div>
        </div>
    </section>

    <ConfirmFightsMoveModal
        ref="confirmFightsMoveModal"
        :mats="mats"
        :source-mat-id="moveFightsModeMatId"
        @moveTargetConfirmed="onMoveTargetConfirmed"
    />

    <EnterBreakModal ref="insertBreakModal" @result="confirmInsertedBreak" />

    <FightReorderModal ref="fightsReorderModal" @onReorderedFights="reorderFights" />

    <FinishedFightsForMatModal
        ref="finishedFightsModal"
        :event-id="eventId"
        :event-timezone="eventTimezone"
        @fightReverted="refreshFightsForMat"
    />

    <ReshuffleCompetitorsModal ref="reshuffleCompetitorsModal" :event-id="eventId" @reshuffleConfirmed="refreshFightsForMat" />
</template>

<script setup lang="ts">
    import history from "../Common/history";
    import translatorMixin from "../Common/translatorMixin";
    import FightsOnMat from "./FightsOnMat.vue";
    import ConfirmFightsMoveModal from "./ConfirmFightsMoveModal.vue";
    import MatSelectionBar, { SelectedMatEvent } from "../Common/MatSelectionBar.vue";
    import {
        eventBus,
        FINISHED_FIGHTS_FOR_MAT_MODAL,
        INSERT_BREAK_BEFORE_FIGHT,
        OPEN_RESHUFFLE_COMPETITORS_MODAL,
        REORDER_FIGHTS_MODAL,
    } from "./figsManageEventBus";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import FightReorderModal from "./FightsReorderModal.vue";
    import FinishedFightsForMatModal from "./FinishedFightsForMatModal.vue";
    import { computed, onMounted, ref, watch } from "vue";
    import schedulesApi from "../Api/schedules";
    import { handleError } from "../Common/errorHandler";
    import fightsApi from "../Api/fights";
    import matsApi from "../Api/mats";
    import store from "../Common/store";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import EnterBreakModal from "./EnterBreakModal.vue";
    import ReshuffleCompetitorsModal from "./ReshuffleCompetitorsModal.vue";
    import { FightQueueStatus, OrganizerComplexFight } from "../Common/Fights/types";
    import { Schedule } from "../Common/Schedule/types";
    import { Mat } from "../Common/types";
    import ScheduleSelect from "../Common/ScheduleSelect.vue";
    import InlineSpinner from "../Common/InlineSpinner.vue";

    const { translate } = translatorMixin.methods;

    const historyInstance = history.getInstance(window);

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const fightsForMat = ref<Record<number, OrganizerComplexFight[] | null>>({});
    const fightQueueStatusForMat = ref<Record<number, FightQueueStatus | null>>({});

    const refreshFightsForMat = matId => {
        return fightsApi
            .getIncomingFights(props.eventId, matId, selectedScheduleId.value)
            .then(fightsResponse => {
                fightsForMat.value[matId] = fightsResponse.result;
                fightQueueStatusForMat.value[matId] = fightsResponse.fightQueueStatus;
            })
            .catch(err => {
                handleError(err);
                return [];
            });
    };

    // SHOWING ACTIVE SCHEDULE

    const activeSchedule = ref<Schedule | null>(null);
    const allSchedules = ref<Schedule[]>([]);
    const selectedScheduleId = ref<number | undefined>(undefined);

    function fetchActiveSchedule() {
        return schedulesApi
            .fetchActiveSchedule(props.eventId)
            .then(response => {
                activeSchedule.value = response.active_schedule;
                allSchedules.value = response.all_schedules;
                // Set the selected schedule to the active schedule by default
                if (activeSchedule.value) {
                    selectedScheduleId.value = activeSchedule.value.id;
                }
            })
            .catch(handleError);
    }

    // Watch for schedule selection changes and refresh all visible mats
    const changingScheduleInProgress = ref(false);
    watch(selectedScheduleId, (newScheduleId, oldScheduleId) => {
        if (newScheduleId !== oldScheduleId && oldScheduleId !== undefined) {
            // Refresh fights for all currently selected mats
            const selectedMats = Object.keys(fightsForMat.value).filter(matId => {
                return fightsForMat.value[matId] !== null;
            });

            changingScheduleInProgress.value = true;

            return Promise.all(selectedMats.map(matId => refreshFightsForMat(parseInt(matId, 10))))
                .catch(handleError)
                .finally(() => {
                    changingScheduleInProgress.value = false;
                });
        }
        return Promise.resolve();
    });

    // MAT SELECTION BAR

    const matsLoaded = ref(false);
    const mats = ref<Mat[]>([]);

    function fetchAllMats() {
        return matsApi
            .getMatsListing(props.eventId)
            .then(matsResponse => {
                mats.value = matsResponse;
                matsLoaded.value = true;
            })
            .catch(handleError);
    }

    function updateLocation() {
        const selectedIds = Object.keys(fightsForMat.value).filter(matId => {
            return !!fightsForMat.value[matId];
        });
        historyInstance.setQueryParam("mat", selectedIds.join(","));
    }

    /**
     * Event handler for MatSelectionBar component
     */
    function selectedMat(eventData: SelectedMatEvent[]) {
        if (eventData.length !== 1) {
            return;
        }

        const { matId, state, buttonReference: matButton } = eventData[0];

        matButton.disable();

        if (state) {
            refreshFightsForMat(matId).then(() => {
                updateLocation();
                matButton.enable();
            });
        } else {
            fightsForMat.value[matId] = null;
            fightQueueStatusForMat.value[matId] = null;
            matButton.enable();
            updateLocation();
        }
    }

    // MOVING FIGHTS FROM ONE MAT TO ANOTHER

    const confirmFightsMoveModal = ref<InstanceType<typeof ConfirmFightsMoveModal> | null>(null);
    const moveFightsModeMatId = ref(0);
    const selectedFightsToMove = ref({});

    function onMoveFights(mat, newSelectedFightsToMove) {
        moveFightsModeMatId.value = mat.id;
        selectedFightsToMove.value = newSelectedFightsToMove;
        confirmFightsMoveModal.value?.open();
    }

    function getEnabledFightIds() {
        return Object.keys(selectedFightsToMove.value)
            .filter(value => {
                return selectedFightsToMove.value[value];
            })
            .map(Number);
    }

    const isMatToggled = matId => !!fightsForMat.value[matId];

    function onMoveTargetConfirmed(target) {
        const fightIds = getEnabledFightIds();

        return fightsApi
            .moveFightsToDifferentMat(props.eventId, fightIds, target.targetMatId, target.queueTargetPlace)
            .then(() => {
                const sourceMatId = moveFightsModeMatId.value;
                const targetMatId = target.targetMatId;

                if (isMatToggled(sourceMatId)) {
                    refreshFightsForMat(sourceMatId);
                }
                if (isMatToggled(targetMatId)) {
                    refreshFightsForMat(targetMatId);
                }

                moveFightsModeMatId.value = 0;
                selectedFightsToMove.value = {};
            })
            .catch(handleError);
    }

    // REORDERING FIGHTS

    const fightsReorderModal = ref<InstanceType<typeof FightReorderModal> | null>(null);

    function reorderFights(matId, fightIds) {
        return fightsApi
            .reorderFights(props.eventId, fightIds)
            .then(() => {
                return refreshFightsForMat(matId);
            })
            .catch(handleError);
    }

    onMounted(() => {
        eventBus.$on(REORDER_FIGHTS_MODAL, ({ fights, mat }) => {
            fightsReorderModal.value?.open(fights, mat);
        });
    });

    // COLUMN VIEW TOGGLE

    const twoColumns = ref(false);

    function getColumnsClass() {
        return twoColumns.value ? "is-6" : "is-12";
    }

    // INSERTING BREAKS

    function confirmInsertedBreak(value, fight) {
        return fightsApi
            .insertBreakBeforeFight(props.eventId, fight.id, parseInt(value, 10) * 60)
            .then(() => {
                return refreshFightsForMat(fight.matId);
            })
            .catch(handleError);
    }

    function getTwoColumnsKey() {
        return "fightManagement_twoColumns_" + props.eventId;
    }

    watch(twoColumns, () => {
        store.setBoolean(getTwoColumnsKey(), twoColumns.value);
    });

    onMounted(() => {
        twoColumns.value = store.getBoolean(getTwoColumnsKey());
    });

    const finishedFightsModal = ref<InstanceType<typeof FinishedFightsForMatModal> | null>(null);
    const insertBreakModal = ref<InstanceType<typeof EnterBreakModal> | null>(null);

    // RESHUFFLING COMPETITORS

    const reshuffleCompetitorsModal = ref<InstanceType<typeof ReshuffleCompetitorsModal> | null>(null);

    onMounted(() => {
        eventBus.$on(OPEN_RESHUFFLE_COMPETITORS_MODAL, fightId => {
            reshuffleCompetitorsModal.value?.openForFight(fightId);
        });
    });

    // GENERIC INITIALIZATION

    const initialLoadFinished = computed(() => !!matsLoaded.value && !!activeSchedule.value);
    const selectedFilteredMats = computed(() => mats.value.filter(mat => isMatToggled(mat.id)));

    onMounted(() => {
        eventBus.$on(FINISHED_FIGHTS_FOR_MAT_MODAL, matId => {
            finishedFightsModal.value?.open(matId);
        });
        eventBus.$on(INSERT_BREAK_BEFORE_FIGHT, fight => {
            insertBreakModal.value?.open(fight);
        });

        return Promise.all([fetchAllMats(), fetchActiveSchedule()]).catch(handleError);
    });
</script>

<style scoped></style>
